import React, { useState } from 'react';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Collapse from '@mui/material/Collapse';
import DashboardIcon from '@mui/icons-material/Dashboard';
import DevicesIcon from '@mui/icons-material/Devices';
import GroupIcon from '@mui/icons-material/Group';
import SettingsIcon from '@mui/icons-material/Settings';
import ApiIcon from '@mui/icons-material/Api';
import AltRouteIcon from '@mui/icons-material/AltRoute';
import SpeedIcon from '@mui/icons-material/Speed';
import StorageIcon from '@mui/icons-material/Storage';
import SettingsSuggestIcon from '@mui/icons-material/SettingsSuggest';
import AppsIcon from '@mui/icons-material/Apps';
import CleaningServicesIcon from '@mui/icons-material/CleaningServices';
import CampaignIcon from '@mui/icons-material/Campaign';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import LogoutIcon from '@mui/icons-material/Logout';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Avatar from '@mui/material/Avatar';
import { useQuery } from '@tanstack/react-query';
import { fetchUserDetails } from '../services/api';

function UserMenu({ fullName, onLogout }: { fullName?: string | null; onLogout?: () => void }) {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  return (
    <>
      <Button onClick={(e) => setAnchorEl(e.currentTarget)} sx={{ textTransform: 'none', color: '#fff' }} startIcon={<Avatar sx={{ width: 28, height: 28 }}>{fullName ? fullName.charAt(0) : '?'}</Avatar>}>
        <span style={{ marginLeft: 8 }}>{fullName ?? ''}</span>
      </Button>
      <Menu anchorEl={anchorEl} open={open} onClose={() => setAnchorEl(null)} PaperProps={{ sx: { bgcolor: '#ffffff' } }}>
        <MenuItem sx={{ bgcolor: '#ffffff' }} onClick={() => { setAnchorEl(null); onLogout?.(); }}>
          <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>
          Logout
        </MenuItem>
      </Menu>
    </>
  );
}

const drawerWidth = 280;

export default function Layout({
  children,
  selectedTab,
  onTabChange,
  showSidebar = true,
  onLogout,
}: {
  children: React.ReactNode;
  selectedTab: string;
  onTabChange: (tab: string) => void;
  showSidebar?: boolean;
  onLogout?: () => void;
}) {
  const [maintenanceOpen, setMaintenanceOpen] = useState(false);
  const [pushConfigOpen, setPushConfigOpen] = useState(false);
  // control whether the left sidebar is visible (user can toggle)
  const [drawerOpen, setDrawerOpen] = useState<boolean>(true);

  const { data: userDetails } = useQuery({ queryKey: ['user', 'details'], queryFn: fetchUserDetails, staleTime: 1000 * 60 * 5, retry: false, refetchOnWindowFocus: false, enabled: Boolean(showSidebar) });
  const hasTgmaxxTenant = React.useMemo(() => {
    return Boolean(userDetails?.userTenants?.some((ut: any) => ut?.tenant?.name?.toLowerCase?.() === 'tgmaxx'));
  }, [userDetails]);

  const toggleMaintenance = () => {
    setMaintenanceOpen((v) => !v);
  };
  const togglePushConfig = () => {
    setPushConfigOpen((v) => !v);
  };

  React.useEffect(() => {
    if (selectedTab.startsWith('PushConfig') && !pushConfigOpen) {
      setPushConfigOpen(true);
    }
  }, [selectedTab, pushConfigOpen]);
  return (
    <div style={{ display: 'flex' }}>
      <AppBar position="fixed" color="transparent" sx={{ zIndex: (theme) => theme.zIndex.drawer + 1, boxShadow: 'none' }}>
        <Toolbar>
          {/* small logo served from public/ placed before the app title */}
          <img src="/ATV%20Bridge%20Logo.png" alt="ATV Bridge logo" style={{ width: 45, height: 45, marginRight: 12 }} />
          <Typography variant="h6" component="div" sx={{ flexGrow: 1, color: '#fff' }}>
            ATV Bridge
          </Typography>
          {/* Right-side user control: avatar + full name, opens menu with Logout */}
          {showSidebar && (
            <Box sx={{ display: 'flex', alignItems: 'center', ml: 2 }}>
              <UserMenu onLogout={onLogout} fullName={userDetails?.fullName} />
            </Box>
          )}
        </Toolbar>
      </AppBar>

      {showSidebar && drawerOpen && (
        <Drawer
          variant="permanent"
          sx={{
            width: drawerWidth,
            flexShrink: 0,
            // keep the drawer anchored under the AppBar (AppBar height ~= 64px -> spacing(8))
            '& .MuiDrawer-paper': { width: drawerWidth, boxSizing: 'border-box', mt: 8, paddingTop: 0 },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', p: 1 }}>
            <IconButton size="small" onClick={() => setDrawerOpen(false)} aria-label="Collapse sidebar">
              <ChevronLeftIcon />
            </IconButton>
          </Box>
          <List>
            <ListItemButton selected={selectedTab === 'Dashboard'} onClick={() => onTabChange('Dashboard')}>
              <ListItemIcon>
                <DashboardIcon />
              </ListItemIcon>
              <ListItemText primary="Dashboard" />
            </ListItemButton>
            <ListItemButton selected={selectedTab === 'Devices'} onClick={() => onTabChange('Devices')}>
              <ListItemIcon>
                <DevicesIcon />
              </ListItemIcon>
              <ListItemText primary="Devices" />
            </ListItemButton>
            <ListItemButton selected={selectedTab === 'Groups'} onClick={() => onTabChange('Groups')}>
              <ListItemIcon>
                <GroupIcon />
              </ListItemIcon>
              <ListItemText primary="Groups" />
            </ListItemButton>
            {/* Maintenance collapsible menu (TGMaxx only) */}
            {hasTgmaxxTenant && (
              <>
                <ListItemButton
                  selected={selectedTab.startsWith('Maintenance')}
                  onClick={toggleMaintenance}
                >
                  <ListItemIcon>
                    <SettingsIcon />
                  </ListItemIcon>
                  <ListItemText primary="Maintenance" />
                  {maintenanceOpen ? <ExpandLess /> : <ExpandMore />}
                </ListItemButton>
                <Collapse in={maintenanceOpen} timeout="auto" unmountOnExit>
                  <List
                    component="div"
                    disablePadding
                    sx={{
                      ml: 3.5,
                      pl: 1.5,
                      borderLeft: '2px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <ListItemButton
                      sx={{
                        pl: 3.5,
                        position: 'relative',
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          left: -12,
                          top: '50%',
                          width: 'calc(14%)',
                          borderBottom: '2px solid',
                          borderColor: 'divider',
                          transform: 'translateY(-50%)',
                        },
                      }}
                      selected={selectedTab === 'Maintenance/Ping'}
                      onClick={() => onTabChange('Maintenance/Ping')}
                    >
                      <ListItemIcon>
                        <ApiIcon />
                      </ListItemIcon>
                      <ListItemText primary="Ping" />
                    </ListItemButton>
                    <ListItemButton
                      sx={{
                        pl: 3.5,
                        position: 'relative',
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          left: -12,
                          top: '50%',
                          width: 'calc(14%)',
                          borderBottom: '2px solid',
                          borderColor: 'divider',
                          transform: 'translateY(-50%)',
                        },
                      }}
                      selected={selectedTab === 'Maintenance/TraceRoute'}
                      onClick={() => onTabChange('Maintenance/TraceRoute')}
                    >
                      <ListItemIcon>
                        <AltRouteIcon />
                      </ListItemIcon>
                      <ListItemText primary="Trace Route" />
                    </ListItemButton>
                    <ListItemButton
                      sx={{
                        pl: 3.5,
                        position: 'relative',
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          left: -12,
                          top: '50%',
                          width: 'calc(14%)',
                          borderBottom: '2px solid',
                          borderColor: 'divider',
                          transform: 'translateY(-50%)',
                        },
                      }}
                      selected={selectedTab === 'Maintenance/SpeedTest'}
                      onClick={() => onTabChange('Maintenance/SpeedTest')}
                    >
                      <ListItemIcon>
                        <SpeedIcon />
                      </ListItemIcon>
                      <ListItemText primary="Speed Test" />
                    </ListItemButton>
                  </List>
                </Collapse>
              </>
            )}
            {hasTgmaxxTenant && (
              <>
                <ListItemButton
                  selected={selectedTab.startsWith('PushConfig')}
                  onClick={togglePushConfig}
                >
                  <ListItemIcon>
                    <SettingsSuggestIcon />
                  </ListItemIcon>
                  <ListItemText primary="Push Configuration" />
                  {pushConfigOpen ? <ExpandLess /> : <ExpandMore />}
                </ListItemButton>
                <Collapse in={pushConfigOpen} timeout="auto" unmountOnExit>
                  <List
                    component="div"
                    disablePadding
                    sx={{
                      ml: 3.5,
                      pl: 1.5,
                      borderLeft: '2px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <ListItemButton
                      sx={{
                        pl: 3.5,
                        position: 'relative',
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          left: -12,
                          top: '50%',
                          width: 'calc(14%)',
                          borderBottom: '2px solid',
                          borderColor: 'divider',
                          transform: 'translateY(-50%)',
                        },
                      }}
                      selected={selectedTab === 'PushConfig/Launcher'}
                      onClick={() => onTabChange('PushConfig/Launcher')}
                    >
                      <ListItemIcon>
                        <AppsIcon />
                      </ListItemIcon>
                      <ListItemText primary="Launcher" />
                    </ListItemButton>
                    <ListItemButton
                      sx={{
                        pl: 3.5,
                        position: 'relative',
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          left: -12,
                          top: '50%',
                          width: 'calc(14%)',
                          borderBottom: '2px solid',
                          borderColor: 'divider',
                          transform: 'translateY(-50%)',
                        },
                      }}
                      selected={selectedTab === 'PushConfig/CleanAppData'}
                      onClick={() => onTabChange('PushConfig/CleanAppData')}
                    >
                      <ListItemIcon>
                        <CleaningServicesIcon />
                      </ListItemIcon>
                      <ListItemText primary="Clean App Data" />
                    </ListItemButton>
                    <ListItemButton
                      sx={{
                        pl: 3.5,
                        position: 'relative',
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          left: -12,
                          top: '50%',
                          width: 'calc(14%)',
                          borderBottom: '2px solid',
                          borderColor: 'divider',
                          transform: 'translateY(-50%)',
                        },
                      }}
                      selected={selectedTab === 'PushConfig/PushMessage'}
                      onClick={() => onTabChange('PushConfig/PushMessage')}
                    >
                      <ListItemIcon>
                        <CampaignIcon />
                      </ListItemIcon>
                      <ListItemText primary="Push Message" />
                    </ListItemButton>
                  </List>
                </Collapse>
              </>
            )}
            <ListItemButton selected={selectedTab === 'AppData'} onClick={() => onTabChange('AppData')}>
              <ListItemIcon>
                <StorageIcon />
              </ListItemIcon>
              <ListItemText primary="Clear App Data" />
            </ListItemButton>
          </List>
        </Drawer>
      )}

      {/* floating opener when drawer is hidden but sidebar is enabled */}
      {showSidebar && !drawerOpen && (
        <Box sx={{ position: 'fixed', top: 70, left: 8, zIndex: (theme) => theme.zIndex.drawer + 2 }}>
          <IconButton size="small" color="primary" onClick={() => setDrawerOpen(true)} aria-label="Open sidebar" sx={{ bgcolor: '#ffffff', boxShadow: 1 }}>
            <MenuIcon />
          </IconButton>
        </Box>
      )}

  <Container maxWidth="xl" sx={{ mt: 8, mb: 4, width: '100%' }} className="cohesive-surface no-card-gap">
        <Paper className="page-card" elevation={2} sx={{ p: 2 }}>
          {children}
        </Paper>
      </Container>
    </div>
  );
}
